import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bookRide,
  cancelRide,
  confirmRideApi,
  finishRideApi,
  getRide,
  startRideApi,
} from '../lib/api';
import { segmentPrice, subtripDistanceKm } from '@waylo/shared';
import { inConfirmWindow } from '../lib/datetime';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';
import { jsLocale, translateError, useT } from '../lib/i18n';
import { BackButton } from '../lib/BackButton';
import { MainButton } from '../lib/MainButton';
import { Card, CardHint, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { ReliabilityBadge } from '../components/ReliabilityBadge';
import { PickupMap, mapAvailable } from '../components/PickupMap';
import { TextArea, TextInput } from '../components/TextInput';
import { Select } from '../components/Select';
import { TripStatusPanel } from '../components/TripStatusPanel';
import { getWebApp, hapticError, hapticSuccess, hapticWarning, showConfirm } from '../lib/telegram';

const LUGGAGE_RANK: Record<string, number> = { small: 1, medium: 2, large: 3 };
const luggageRank = (v: string): number => LUGGAGE_RANK[v] ?? 1;

export function RideDetailPage({ rideId }: { rideId: string }): JSX.Element {
  const { navigate, search } = useRouter();
  const { me } = useSession();
  const { t, locale } = useT();
  const qc = useQueryClient();
  const [message, setMessage] = useState('');
  const [luggage, setLuggage] = useState<'small' | 'medium' | 'large'>('small');
  const [seats, setSeats] = useState(1);
  const [companions, setCompanions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Partial-trip stops (null = ride's origin/destination, i.e. full trip).
  // Pre-filled from ?from=&to= when arriving from a sub-path search result.
  const [boardId, setBoardId] = useState<string | null>(() => search.get('from'));
  const [alightId, setAlightId] = useState<string | null>(() => search.get('to'));

  const q = useQuery({ queryKey: ['ride', rideId], queryFn: () => getRide(rideId) });

  const book = useMutation({
    mutationFn: () => {
      const ride = q.data!;
      const pc = [ride.route.origin, ...ride.viaCities, ride.route.destination];
      const fIdx = pc.findIndex((c) => c.id === (boardId ?? ride.route.origin.id));
      const tIdx = pc.findIndex((c) => c.id === (alightId ?? ride.route.destination.id));
      const partial =
        ride.allowPartial &&
        fIdx >= 0 &&
        tIdx >= 0 &&
        fIdx < tIdx &&
        !(fIdx === 0 && tIdx === pc.length - 1);
      return bookRide(
        rideId,
        seats,
        message.trim() || undefined,
        luggage,
        companions
          .slice(0, Math.max(0, seats - 1))
          .map((c) => c.trim())
          .filter((c) => c.length > 0),
        partial ? { fromCityId: pc[fIdx]!.id, toCityId: pc[tIdx]!.id } : undefined,
      );
    },
    onSuccess: async () => {
      hapticSuccess();
      await qc.invalidateQueries({ queryKey: ['ride', rideId] });
      navigate('/my/bookings');
    },
    onError: (e: Error) => {
      hapticError();
      setError(translateError(t, e));
    },
  });

  const cancel = useMutation({
    mutationFn: () => cancelRide(rideId),
    onSuccess: async () => {
      hapticSuccess();
      await qc.invalidateQueries({ queryKey: ['ride', rideId] });
      await qc.invalidateQueries({ queryKey: ['my-rides'] });
      navigate('/my/rides');
    },
    onError: (e: Error) => {
      // Previously missing — a rejected cancel (e.g. ride already departed)
      // failed silently, which looked like "nothing happens".
      hapticError();
      setError(translateError(t, e));
    },
  });

  const confirm = useMutation({
    mutationFn: () => confirmRideApi(rideId),
    onSuccess: async () => {
      hapticSuccess();
      await qc.invalidateQueries({ queryKey: ['ride', rideId] });
    },
    onError: (e: Error) => {
      hapticError();
      setError(translateError(t, e));
    },
  });

  const start = useMutation({
    mutationFn: () => startRideApi(rideId),
    onMutate: async () => {
      // Optimistic: flip ride -> 'departed' so the driver immediately sees the
      // "Finish" action instead of re-tapping "Start" during the round-trip.
      await qc.cancelQueries({ queryKey: ['ride', rideId] });
      const prev = qc.getQueryData<typeof q.data>(['ride', rideId]);
      if (prev) qc.setQueryData(['ride', rideId], { ...prev, status: 'departed' });
      return { prev };
    },
    onSuccess: () => {
      hapticSuccess();
    },
    onError: (e: Error, _vars, ctx) => {
      hapticError();
      if (ctx?.prev) qc.setQueryData(['ride', rideId], ctx.prev);
      setError(translateError(t, e));
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['ride', rideId] });
      await qc.invalidateQueries({ queryKey: ['my-rides'] });
    },
  });

  const finish = useMutation({
    mutationFn: () => finishRideApi(rideId),
    onMutate: async () => {
      // Optimistic: flip ride -> 'completed' so the trip visibly closes at once.
      await qc.cancelQueries({ queryKey: ['ride', rideId] });
      const prev = qc.getQueryData<typeof q.data>(['ride', rideId]);
      if (prev) qc.setQueryData(['ride', rideId], { ...prev, status: 'completed' });
      return { prev };
    },
    onSuccess: () => {
      hapticSuccess();
    },
    onError: (e: Error, _vars, ctx) => {
      hapticError();
      if (ctx?.prev) qc.setQueryData(['ride', rideId], ctx.prev);
      setError(translateError(t, e));
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['ride', rideId] });
      await qc.invalidateQueries({ queryKey: ['my-rides'] });
      await qc.invalidateQueries({ queryKey: ['my-bookings'] });
    },
  });

  if (q.isLoading) {
    return (
      <div className="mx-auto max-w-md p-6">
        <BackButton />
        <div className="space-y-3">
          <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
          <div className="h-16 animate-pulse rounded-card bg-tg-bg-secondary" />
          <div className="h-20 animate-pulse rounded-card bg-tg-bg-secondary" />
        </div>
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="mx-auto max-w-md p-6">
        <BackButton />
        <p className="text-destructive">{t('ride.cantLoad')}</p>
      </div>
    );
  }
  const r = q.data;
  const isDriver = me?.id === r.driver.id;
  const isDark = getWebApp()?.colorScheme === 'dark';
  const departure = new Date(r.departureAt);
  const nameKey: 'nameSr' | 'nameEn' = locale === 'en' ? 'nameEn' : 'nameSr';
  const fmtLocale = jsLocale(locale);

  // Partial-trip booking: ordered stop list + distance-proportional price.
  const pathCities = [r.route.origin, ...r.viaCities, r.route.destination];
  const fromIdx = Math.max(0, pathCities.findIndex((c) => c.id === (boardId ?? r.route.origin.id)));
  const rawToIdx = pathCities.findIndex((c) => c.id === (alightId ?? r.route.destination.id));
  const toIdx = rawToIdx < 0 ? pathCities.length - 1 : rawToIdx;
  const segValid = fromIdx < toIdx;
  const coords = pathCities.map((c) => ({ lat: c.lat, lng: c.lng }));
  const totalKm = subtripDistanceKm(coords, 0, coords.length - 1);
  const segKm = segValid ? subtripDistanceKm(coords, fromIdx, toIdx) : totalKm;
  const displayPrice = r.allowPartial ? segmentPrice(r.proposedCost, segKm, totalKm) : r.proposedCost;

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <BackButton />

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-tg-hint">
          {departure.toLocaleDateString(fmtLocale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}
        </p>
        <h1 className="text-2xl font-bold text-tg-text">
          {r.route.origin[nameKey]} → {r.route.destination[nameKey]}
        </h1>
        {r.viaCities.length > 0 && (
          <p className="text-sm text-tg-hint">
            {t('ride.via.long', {
              stops: r.viaCities.map((c) => c[nameKey]).join(' → '),
            })}
          </p>
        )}
        <p className="text-tg-hint">
          {departure.toLocaleTimeString(fmtLocale, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })}
          {r.flexibilityMinutes > 0 ? ` (± ${r.flexibilityMinutes}m)` : ''}
        </p>
      </header>

      <Card className="mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <CardTitle>{t('ride.cost')}</CardTitle>
          <Badge tone="success">
            {Math.round(r.proposedCost).toLocaleString()} {r.currency} / {t('common.perSeat')}
          </Badge>
        </div>
        <CardHint>
          {t('ride.cost.hint', {
            ref: Math.round(r.referenceCost).toLocaleString(),
            currency: r.currency,
          })}
        </CardHint>
      </Card>

      <Card className="mt-3 space-y-2">
        <div className="flex items-center gap-3">
          {r.driver.avatarUrl ? (
            <img src={r.driver.avatarUrl} alt="" className="h-10 w-10 rounded-full" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-navy text-brand-cream">
              {(r.driver.displayName ?? '?').slice(0, 1)}
            </div>
          )}
          <div className="flex-1">
            <button
              type="button"
              onClick={() => navigate(isDriver ? '/profile' : `/profile/${r.driver.id}`)}
              className="font-semibold text-tg-text"
            >
              {r.driver.displayName}
            </button>
            <p className="text-xs text-tg-hint">
              {t('ride.driverRides', { n: r.driver.ridesCompleted })}
              {r.driver.ratingAvg !== null && ` · ★ ${r.driver.ratingAvg.toFixed(1)}`}
            </p>
            <div className="mt-0.5">
              <ReliabilityBadge
                score={r.driver.reliability}
                ridesCompleted={r.driver.ridesCompleted}
              />
            </div>
          </div>
          {r.driver.badges.includes('community_verified') && (
            <Badge tone="brand">{t('ride.verified')}</Badge>
          )}
        </div>
        {r.driver.bio && <CardHint>{r.driver.bio}</CardHint>}
      </Card>

      <Card className="mt-3 space-y-1">
        <CardTitle>{t('ride.preferences')}</CardTitle>
        <div className="flex flex-wrap gap-2 pt-1">
          {r.preferences.womenOnly && <Badge tone="brand">{t('ride.pref.womenOnly')}</Badge>}
          <Badge tone="neutral">
            {r.preferences.smokingAllowed ? t('ride.pref.smoking.yes') : t('ride.pref.smoking.no')}
          </Badge>
          <Badge tone="neutral">
            {r.preferences.musicAllowed ? t('ride.pref.music.yes') : t('ride.pref.music.no')}
          </Badge>
          <Badge tone="neutral">
            {r.preferences.petsAllowed ? t('ride.pref.pets.yes') : t('ride.pref.pets.no')}
          </Badge>
          <Badge tone="neutral">
            {t('ride.pref.luggage')}: {t(`luggage.${r.preferences.luggageMax}` as 'luggage.medium')}
          </Badge>
          {/* Driver car-comfort — only positive chips (absence ≠ "no"). */}
          {r.driver.carAc && <Badge tone="neutral">{t('comfort.ac')}</Badge>}
          {r.driver.carCharger && <Badge tone="neutral">{t('comfort.charger')}</Badge>}
          {r.driver.carMiddleSeat && <Badge tone="neutral">{t('comfort.middleSeat')}</Badge>}
        </div>
        {r.pickupNotes && (
          <CardHint>
            {t('ride.pickup')}: {r.pickupNotes}
          </CardHint>
        )}
      </Card>

      {(r.pickupTitle || r.pickupLat != null) && (
        <Card className="mt-3 space-y-2">
          <CardTitle>{t('pickup.cardTitle')}</CardTitle>
          {r.pickupTitle && <p className="text-sm text-tg-text">📍 {r.pickupTitle}</p>}
          {r.pickupLat != null && r.pickupLng != null ? (
            <>
              {mapAvailable() && (
                <PickupMap
                  lat={r.pickupLat}
                  lng={r.pickupLng}
                  centerLat={r.pickupLat}
                  centerLng={r.pickupLng}
                  dark={isDark}
                />
              )}
              <button
                type="button"
                onClick={() => {
                  const url = `https://www.google.com/maps/search/?api=1&query=${r.pickupLat},${r.pickupLng}`;
                  const tg = getWebApp();
                  if (tg?.openLink) tg.openLink(url);
                  else window.open(url, '_blank');
                }}
                className="text-sm font-medium text-tg-link underline"
              >
                {t('pickup.directions')}
              </button>
            </>
          ) : (
            <CardHint>{t('pickup.afterBooking')}</CardHint>
          )}
        </Card>
      )}

      <TripStatusPanel
        rideId={r.id}
        departureAt={r.departureAt}
        asDriver={isDriver}
        canRead={isDriver}
      />

      {['published', 'full', 'departed'].includes(r.status) && inConfirmWindow(r.departureAt) && (
        <Card className="mt-3 space-y-2">
          <CardTitle>{t('confirm.title')}</CardTitle>
          {isDriver ? (
            r.driverConfirmedAt ? (
              <p className="text-sm font-medium text-brand-green">✓ {t('confirm.driver.done')}</p>
            ) : (
              <>
                <CardHint>{t('confirm.driver.pending')}</CardHint>
                <button
                  type="button"
                  onClick={() => confirm.mutate()}
                  disabled={confirm.isPending}
                  className="w-full rounded-input bg-brand-green py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {t('confirm.driver.cta')}
                </button>
              </>
            )
          ) : (
            <p
              className={
                'text-sm font-medium ' +
                (r.driverConfirmedAt ? 'text-brand-green' : 'text-[var(--warning)]')
              }
            >
              {r.driverConfirmedAt ? t('confirm.driverConfirmed') : t('confirm.driverPending')}
            </p>
          )}
        </Card>
      )}

      {!isDriver && r.status === 'published' && (
        <Card className="mt-3 space-y-2">
          {r.allowPartial && r.viaCities.length > 0 && (
            <div className="space-y-2">
              <CardTitle>{t('segment.title')}</CardTitle>
              <CardHint>{t('segment.hint')}</CardHint>
              <Select
                id="board"
                label={t('segment.board')}
                value={boardId ?? r.route.origin.id}
                onChange={(e) => setBoardId(e.target.value)}
                options={pathCities.slice(0, -1).map((c) => ({ value: c.id, label: c[nameKey] }))}
              />
              <Select
                id="alight"
                label={t('segment.alight')}
                value={alightId ?? r.route.destination.id}
                onChange={(e) => setAlightId(e.target.value)}
                options={pathCities.slice(1).map((c) => ({ value: c.id, label: c[nameKey] }))}
              />
              {!segValid && <p className="text-xs text-destructive">{t('segment.invalid')}</p>}
              <div className="flex items-center justify-between rounded-input bg-tg-bg-secondary p-2.5">
                <span className="text-sm text-tg-hint">{t('segment.price')}</span>
                <span className="text-sm font-semibold text-tg-text">
                  {displayPrice.toLocaleString()} {r.currency} / {t('common.perSeat')}
                </span>
              </div>
            </div>
          )}
          <Select
            id="seats"
            label={t('booking.seats')}
            value={String(seats)}
            onChange={(e) => {
              const n = Number(e.target.value);
              setSeats(n);
              // Keep companion slots in sync with seats − 1.
              setCompanions((prev) => prev.slice(0, Math.max(0, n - 1)));
            }}
            options={Array.from(
              { length: Math.min(r.seatsRemaining, 4) },
              (_, i) => ({ value: String(i + 1), label: String(i + 1) }),
            )}
          />
          {seats > 1 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-tg-text">{t('booking.companions')}</p>
              <CardHint>{t('booking.companions.hint')}</CardHint>
              {Array.from({ length: seats - 1 }, (_, i) => (
                <TextInput
                  key={i}
                  id={`companion-${i}`}
                  value={companions[i] ?? ''}
                  maxLength={40}
                  placeholder={t('booking.companions.ph', { n: String(i + 2) })}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCompanions((prev) => {
                      const next = [...prev];
                      next[i] = v;
                      return next;
                    });
                  }}
                />
              ))}
            </div>
          )}
          <CardTitle>{t('ride.messageToDriver')}</CardTitle>
          <TextArea
            id="msg"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={200}
            placeholder={t('ride.messageToDriverPh')}
          />
          <Select
            id="luggage"
            label={t('booking.luggage')}
            value={luggage}
            onChange={(e) => setLuggage(e.target.value as 'small' | 'medium' | 'large')}
            options={[
              { value: 'small', label: t('luggage.small') },
              { value: 'medium', label: t('luggage.medium') },
              { value: 'large', label: t('luggage.large') },
            ]}
          />
          {luggageRank(luggage) > luggageRank(r.preferences.luggageMax) && (
            <p className="rounded-input bg-[var(--warning)]/10 p-2 text-xs text-[var(--warning)]">
              {t('booking.luggage.warn', {
                max: t(`luggage.${r.preferences.luggageMax}` as 'luggage.medium'),
              })}
            </p>
          )}
        </Card>
      )}

      {error && (
        <p className="mt-3 rounded-card bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      {isDriver ? (
        <>
          {/* Edit + cancel are available only before the ride starts. */}
          {(r.status === 'published' || r.status === 'full') && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => navigate(`/ride/${r.id}/edit`)}
                className="w-full rounded-input bg-tg-bg-secondary py-3 text-sm font-semibold text-tg-text"
              >
                {t('editRide.title')}
              </button>
            </div>
          )}
          {(r.status === 'published' || r.status === 'full') && (
            <div className="mt-3">
              <button
                type="button"
                onClick={async () => {
                  hapticWarning();
                  const ok = await showConfirm(t('ride.cancelConfirm'));
                  if (ok) cancel.mutate();
                }}
                disabled={cancel.isPending}
                className="w-full rounded-input bg-tg-bg-secondary py-3 text-sm font-semibold text-destructive disabled:opacity-50"
              >
                {t('ride.cancel')}
              </button>
            </div>
          )}
          {(r.status === 'published' || r.status === 'full') && (
            <MainButton
              text={t('ride.start')}
              loading={start.isPending}
              onClick={() => {
                hapticWarning();
                start.mutate();
              }}
            />
          )}
          {r.status === 'departed' && (
            <MainButton
              text={t('ride.finish')}
              loading={finish.isPending}
              onClick={async () => {
                hapticWarning();
                const ok = await showConfirm(t('ride.finishConfirm'));
                if (ok) finish.mutate();
              }}
            />
          )}
        </>
      ) : r.status === 'published' && r.seatsRemaining > 0 ? (
        <MainButton
          text={`${t('ride.requestSeat')} · ${Math.round(displayPrice * seats).toLocaleString()} ${r.currency}`}
          loading={book.isPending}
          disabled={!segValid}
          onClick={() => book.mutate()}
        />
      ) : null}
    </div>
  );
}
